import Reveal from "@/components/Reveal";
import TelemetryOverlay from "@/components/TelemetryOverlay";
import VocabTag from "@/components/VocabTag";
import { GIGA_IMAGES } from "@/lib/images";

/**
 * Dark Factory — cinematic full-bleed image of an autonomous production cell
 * with a subtle telemetry overlay. Intelligence and production acting as one.
 */
export default function DarkFactory() {
  return (
    <section
      id="dark-factory"
      data-testid="dark-factory-section"
      className="relative g1-inverse border-b border-[color:var(--g1-rule)]"
    >
      <div className="relative w-full overflow-hidden">
        <div className="relative aspect-[21/9] md:aspect-[21/8] w-full">
          <img
            src={GIGA_IMAGES.darkFactoryArm}
            alt="Robotic arm handling a pharmaceutical vial in a dark cleanroom"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              filter:
                "grayscale(1) contrast(1.18) brightness(0.55) sepia(0.04)",
            }}
            loading="lazy"
          />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(20,20,19,0.28) 0%, rgba(20,20,19,0.45) 55%, rgba(20,20,19,0.92) 100%)",
            }}
          />
          {/* Telemetry overlay — intelligence integrated into production */}
          <TelemetryOverlay />

          <div className="g1-corners g1-corners-light">
            <span className="tl" />
            <span className="tr" />
            <span className="bl" />
            <span className="br" />
          </div>

          <div className="absolute inset-0 flex flex-col">
            <div className="max-w-[1500px] mx-auto w-full px-5 md:px-10 lg:px-14 pt-6 md:pt-8 flex items-start justify-between">
              <div>
                <div className="g1-label text-[color:var(--g1-bone)]/70">
                  SEC. 04
                </div>
                <div className="g1-eyebrow mt-1 text-[color:var(--g1-bone)]">
                  Dark Factory
                </div>
              </div>
              <div className="flex items-center gap-3 text-right">
                <VocabTag kind="FUSED" dark />
              </div>
            </div>

            <div className="mt-auto max-w-[1500px] mx-auto w-full px-5 md:px-10 lg:px-14 pb-8 md:pb-14">
              <Reveal>
                <h2
                  data-testid="dark-factory-heading"
                  className="font-serif text-[40px] sm:text-[56px] md:text-[96px] lg:text-[128px] leading-[0.95] tracking-[-0.02em] text-[color:var(--g1-bone)] max-w-[16ch]"
                >
                  No lights.{" "}
                  <span className="italic text-[color:var(--g1-olive-muted)]">
                    No shifts.
                  </span>{" "}
                  No deviations.
                </h2>
              </Reveal>
            </div>
          </div>
        </div>
      </div>

      {/* Run-state rail */}
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 lg:px-14 py-14 md:py-20 lg:py-24">
        <Reveal delay={120}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[color:var(--g1-bone)]/20">
            {[
              ["24 / 7", "CONTINUOUS RUN-STATE"],
              ["Δ → 0", "DEVIATION TO ZERO"],
              ["GxP", "PHARMACEUTICAL STD"],
              ["CLOSED", "READ · DECIDE · ACT"],
            ].map(([k, v]) => (
              <div
                key={k}
                className="bg-[color:var(--g1-ink)] px-5 py-8 md:p-8"
              >
                <div className="font-serif text-3xl md:text-5xl leading-none text-[color:var(--g1-bone)]">
                  {k}
                </div>
                <div className="g1-label mt-4 text-[color:var(--g1-bone)]/70">
                  {v}
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={180}>
          <div className="mt-10 md:mt-14 pt-6 border-t border-[color:var(--g1-bone)]/20 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <p className="g1-label text-[color:var(--g1-bone)]/70 max-w-[64ch]">
              Phased deployment · Shadow Mode → Advisory Control → Full Autonomy
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
